import { animate, state, style, transition, trigger } from '@angular/animations';
import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { UserPromotionActions } from '@app/client/account/team/promotion/actions';
import * as fromUserPromotions from '@app/client/account/team/promotion/reducers/promotion.reducer';
import * as fromRoot from '@app/reducers';
import { SmashPromotionComponent } from '@app/shared/modules/smash-promotion/smash-promotion/smash-promotion.component';
import { DialogService } from '@ngneat/dialog';
import { select, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subscription } from 'rxjs';
import { Constant } from 'src/constant';
import { ModalPromotionCreateAddBackgroundComponent } from './modal-promotion-create-add-background/modal-promotion-create-add-background.component';


@Component({
  selector: 'app-promotion-create',
  templateUrl: './promotion-create.component.html',
  styleUrls: ['./promotion-create.component.scss'],
  animations: [
    trigger('headerAnimation', [
      state('in', style({ transform: 'translateX({{startPosition}})' }), { params: { startPosition: '0px' } }),
      state('out', style({ transform: 'translateX({{endPosition}})' }), { params: { endPosition: '-350px' } }),
      transition('in => out', animate('0.4s ease-in-out')),
      transition('out => in', animate('0.4s linear'))
    ]),
  ],
})
export class PromotionCreateComponent implements OnInit {

  @ViewChild(SmashPromotionComponent, { static: true }) promotionComponent: SmashPromotionComponent;

  promotionToCreate$: Observable<any>;
  promotionUpdating$: Observable<boolean>;
  promotionCreating$: Observable<boolean>;
  promotionCreationError$: Observable<any>;
  supportedLanguages$: Observable<any[]>;
  selectedLanguage$: Observable<any>;

  selectedLanguageSync: any;
  subscriptions: Subscription[] = [];
  promotionCreated = false;
  backgroundIsLoading = false;
  selectedMedia: any;
  modalBackgroundOpened = false;
  selectionVideo = false;
  error = false;
  downloadUrl = null;
  promotionCreationForm: FormGroup;
  checkboxStatus = false;
  submitted = false;
  timer: any;
  medias: any[];
  actions: any[] = Constant.callToActionsList;

  targets = [
    { value: 'External', label: 'PREMIUM_PROMOTION_OPTION_EXTERNAL' },
    { value: 'Internal', label: 'PREMIUM_PROMOTION_OPTION_INTERNAL' }
  ];

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private store$: Store<fromRoot.State & fromUserPromotions.State>,
    private readonly fb: FormBuilder,
    private translateService: TranslateService,
    private dialog: DialogService
  ) {
    this.buildForm();

    this.promotionToCreate$ = this.store$.pipe(select(fromUserPromotions.getPromotionToCreate));
    this.promotionCreating$ = this.store$.pipe(select(fromUserPromotions.getPromotionCreating));
    this.promotionCreationError$ = this.store$.pipe(select(fromUserPromotions.getPromotionCreationError));
    this.supportedLanguages$ = this.store$.pipe(select(fromRoot.getSupportedLanguages));
    this.selectedLanguage$ = this.store$.pipe(select(fromRoot.getSelectedLanguage));
  }

  buildForm() {
    this.promotionCreationForm = this.fb.group({
      title: ['', Validators.required],
      teaser: ['', Validators.required],
      text: [''],
      language: ['', Validators.required],
      button: [this.actions[18], Validators.required],
      link: ['', [Validators.required, Validators.pattern(Constant.regexpPromotionLink)]],
      target: [this.targets[0].value, Validators.required],
      status: ['Disabled', Validators.required]
    });
  }

  back() {
    this.route.params.subscribe(params => {
      this.router.navigateByUrl('/account/team/' + params.id + '/promotions');
    });
  }

  initSubscriptions() {
    this.subscriptions.push(
      this.selectedLanguage$.subscribe(res => {
        this.selectedLanguageSync = res ? res : this.translateService.getBrowserLang();
        this.promotionCreationForm.patchValue({ language: this.selectedLanguageSync });
      }),
      this.promotionToCreate$.subscribe(res => {
        this.medias = res.medias;
        this.selectMedia(this.medias[this.medias.length - 1]);
      }),
      this.promotionCreationForm.valueChanges.subscribe(
        value => {
          this.checkboxStatus = value.status;
          value.status = value.status ? 'Enabled' : 'Disabled';
          this.store$.dispatch(UserPromotionActions.updatePromotionToCreate({ data: value }));
        }
      ),
    );
  }

  ngOnInit(): void {
    this.initSubscriptions();
  };

  get f() { return this.promotionCreationForm.controls; }

  onReplay() {
    this.promotionComponent.replay();
  }

  onAddMedia() {
    this.dialog.open(ModalPromotionCreateAddBackgroundComponent, {
      closeButton: true,
      enableClose: true,
      size: 'sm',
    });
  }

  onRemoveMedia(media) {
    this.store$.dispatch(UserPromotionActions.removeMediaFromPromotionToCreate({ media }));
  }

  onDeleteMedia(media, event) {
    this.store$.dispatch(UserPromotionActions.removeMediaFromPromotionToCreate({ media }));
    event.stopPropagation();
  }

  onSubmit() {
    this.submitted = true;
    if (this.promotionCreationForm.valid) {
      this.store$.dispatch(UserPromotionActions.createPromotion());
      this.submitted = false;
    } else {
      return false;
    }
  }

  selectMedia(media) {
    if (media) {
      const index = media.id ? this.medias.findIndex(_media => media.id === _media.id) : this.medias.findIndex(_media => media.object_id === _media.object_id);
      this.selectedMedia = { ...media, index };
    } else {
      return null;
    }
  }

  isMediaSelected(media: any) {
    if (this.selectedMedia) {
      return media.object_id === this.selectedMedia.object_id;
    }
  }

  get indexSelected() {
    return this.selectedMedia ? this.selectedMedia.index : null;
  }

  get isUrlValid() {
    return this.downloadUrl ? (Constant.regexpYoutube.test(this.downloadUrl) || Constant.regexpVimeo.test(this.downloadUrl)) : false;
  }

  resolveSrcUrlString(src: string): string {
    if (src?.startsWith('http')) {
      return `url(${src})`;
    } else {
      return `url(//${src})`;
    }
  }

}
